import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import InputGeneric from "../../components/InputGeneric";
import PageWrapper from "../../components/PageWrapper";
import PreloadFb from "../../components/Preload";
import { useAuth } from "../../contexts/contexts";
import { getApiErrorMessage } from "../../helpers/api/getApiErrorMessage";
import { maskCpfCnpj } from "../../helpers/cpfOrCnpjValidator";
import { defaultToast } from "../../helpers/toast/defaultToast";
import { api } from "../../services/api";
import { SettingsConfigPageStyle } from "../SettingsConfigPage/styles";

type BrandConfiguration = {
    fantasy_name: string;
    corporate_name: string;
    cnpj: string;
    address: string;
    email_sender_name: string;
    email_logo_url: string;
};

const emptyBrand: BrandConfiguration = {
    fantasy_name: "",
    corporate_name: "",
    cnpj: "",
    address: "",
    email_sender_name: "",
    email_logo_url: "",
};

function BrandConfigurationPage() {
    const [isLoading, setIsLoading] = useState(true);
    const [loadingButtonSave, setLoadingButtonSave] = useState(false);
    const [form, setForm] = useState<BrandConfiguration>(emptyBrand);
    const [original, setOriginal] = useState<BrandConfiguration>(emptyBrand);

    const navigate = useNavigate();
    const { hasPermissions, permissions } = useAuth();

    useEffect(() => {
        if (
            hasPermissions("configuracoes.gerais") === false &&
            permissions.length > 0
        ) {
            navigate("/Error");
        }
    }, [permissions]);

    const getBrandConfiguration = async () => {
        try {
            setIsLoading(true);
            const { data } = await api.get("/settings/brand-configuration");

            if (data?.status && data.response) {
                const brand = {
                    fantasy_name: data.response.fantasy_name ?? "",
                    corporate_name: data.response.corporate_name ?? "",
                    cnpj: data.response.cnpj ?? "",
                    address: data.response.address ?? "",
                    email_sender_name: data.response.email_sender_name ?? "",
                    email_logo_url: data.response.email_logo_url ?? "",
                };
                setForm(brand);
                setOriginal(brand);
            }
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(
                    error,
                    "Erro ao buscar identidade da empresa."
                )
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getBrandConfiguration();
    }, []);

    const handleChange = (field: keyof BrandConfiguration, value: string) => {
        setForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const handleSave = async () => {
        try {
            setLoadingButtonSave(true);
            const { data } = await api.put(
                "/settings/brand-configuration",
                form
            );

            if (data?.status) {
                defaultToast("success", "Identidade da empresa atualizada");
                setOriginal(form);
            }
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(
                    error,
                    "Erro ao salvar identidade da empresa."
                )
            );
        } finally {
            setLoadingButtonSave(false);
        }
    };

    const isModified = JSON.stringify(form) !== JSON.stringify(original);

    return (
        <PageWrapper>
            {isLoading ? (
                <PreloadFb />
            ) : (
                <SettingsConfigPageStyle>
                    <h2>Identidade da empresa</h2>
                    <p className="setting-helper-text">
                        Esses dados aparecem no contrato, na tela de assinatura
                        e nos e-mails enviados ao cliente.
                    </p>
                    <div className="settings-block">
                        <div className="inputAndLoadingContainer">
                            <InputGeneric
                                className="inputs"
                                size="small"
                                label="Nome fantasia"
                                value={form.fantasy_name}
                                onChange={(event) =>
                                    handleChange(
                                        "fantasy_name",
                                        event.target.value
                                    )
                                }
                            />
                        </div>
                        <div className="inputAndLoadingContainer">
                            <InputGeneric
                                className="inputs"
                                size="small"
                                label="Razão social"
                                value={form.corporate_name}
                                onChange={(event) =>
                                    handleChange(
                                        "corporate_name",
                                        event.target.value
                                    )
                                }
                            />
                        </div>
                        <div className="inputAndLoadingContainer">
                            <InputGeneric
                                className="inputs"
                                size="small"
                                label="CNPJ"
                                value={form.cnpj}
                                onChange={(event) =>
                                    handleChange(
                                        "cnpj",
                                        maskCpfCnpj(event.target.value)
                                    )
                                }
                            />
                        </div>
                        <div className="inputAndLoadingContainer">
                            <InputGeneric
                                className="inputs"
                                size="small"
                                label="Endereço"
                                value={form.address}
                                onChange={(event) =>
                                    handleChange("address", event.target.value)
                                }
                            />
                        </div>
                        <div className="inputAndLoadingContainer">
                            <InputGeneric
                                className="inputs"
                                size="small"
                                label="Nome no e-mail"
                                helperText="Usado no remetente e no texto 'gerou uma cobrança'."
                                value={form.email_sender_name}
                                onChange={(event) =>
                                    handleChange(
                                        "email_sender_name",
                                        event.target.value
                                    )
                                }
                            />
                        </div>
                        <div className="inputAndLoadingContainer">
                            <InputGeneric
                                className="inputs"
                                size="small"
                                label="URL do logo nos e-mails"
                                helperText="Imagem pública (https). Em branco usa o logo padrão."
                                value={form.email_logo_url}
                                onChange={(event) =>
                                    handleChange(
                                        "email_logo_url",
                                        event.target.value.trim()
                                    )
                                }
                            />
                        </div>
                        {form.email_logo_url && (
                            <img
                                src={form.email_logo_url}
                                alt="Pré-visualização do logo"
                                style={{
                                    display: "block",
                                    width: 200,
                                    maxWidth: "100%",
                                    height: "auto",
                                    marginBottom: 16,
                                }}
                            />
                        )}
                    </div>
                    {isModified && (
                        <div>
                            <LoadingButton
                                variant="contained"
                                onClick={handleSave}
                                loading={loadingButtonSave}
                            >
                                Salvar
                            </LoadingButton>
                        </div>
                    )}
                </SettingsConfigPageStyle>
            )}
        </PageWrapper>
    );
}

export default BrandConfigurationPage;
